// React hooks around the data layer (api.ts / outbox.ts) and the recorder.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { DocType, DocumentSummary, Features, MeResponse, NotesOrder, User } from '../../../shared/types';
import { getDocument, getFeatures, isFromCache, listDocuments, rebuildDocument, subscribeAuth, type ViewDocument } from './api';
import { ApiClientError } from './http';
import { storageWarning, subscribeStorageWarning } from './localdb';
import {
  getStatus,
  opsForDocument,
  startOutbox,
  subscribeDocument,
  subscribeSessionChanged,
  subscribeStatus,
  type OutboxStatus,
} from './outbox';
import { actingUserId, adoptUser, type Ownership } from './session';
import {
  AudioRecorder,
  isRecordingSupported,
  type RecorderProfile,
  type RecorderState,
  type RecordingResult,
} from './recorder';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  /** Slovenian error message. */
  error: string | null;
  /** True when the data came from the offline cache. */
  stale: boolean;
  reload: () => void;
}

const POLL_INTERVAL_MS = 3_000;
/** Collapses bursts of outbox events (e.g. typing) into one reload. */
const EVENT_DEBOUNCE_MS = 50;

function isAbortError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError';
}

function messageOf(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  return 'Podatkov ni bilo mogoče naložiti.';
}

interface LoaderState<T> {
  key: string | null;
  data: T | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
}

/**
 * Prepares a rebuild of the shown data from what is known locally (no request). Resolves a pure
 * function of the current data that returns the new data (null: gone), or undefined to keep it.
 */
type Peek<T> = () => Promise<(current: T) => T | null | undefined>;

/**
 * Shared loading logic: aborts the previous request when a new one starts, keeps the previous
 * data while reloading, reloads on `subscribe` events and after a sign-in, and polls while
 * `shouldPoll(data)` holds and the page is visible. With `peek`, an event first shows the locally
 * rebuilt data and then reloads (stale-while-revalidate), so a change appears at once even on a
 * slow connection. A 401 needs no handling here: the app shows the login (api.ts) and does not
 * show the page meanwhile.
 */
function useLoader<T>(
  key: string | null,
  load: (signal: AbortSignal) => Promise<T | null>,
  subscribe: (listener: () => void) => () => void,
  shouldPoll?: (data: T) => boolean,
  peek?: Peek<T>,
): AsyncState<T> {
  const [state, setState] = useState<LoaderState<T>>(() => ({
    key,
    data: null,
    loading: key !== null,
    error: null,
    stale: false,
  }));
  const loadRef = useRef(load);
  const pollRef = useRef(shouldPoll);
  const subscribeRef = useRef(subscribe);
  const peekRef = useRef(peek);
  useEffect(() => {
    loadRef.current = load;
    pollRef.current = shouldPoll;
    subscribeRef.current = subscribe;
    peekRef.current = peek;
  });

  const controller = useRef<AbortController | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Polling is due but the page was hidden; run as soon as it becomes visible. */
  const pollDue = useRef(false);
  /** Counts loads that delivered data, and peeks started (only the latest peek may land). */
  const loaded = useRef(0);
  const peeks = useRef(0);

  const run = useCallback(() => {
    if (key === null) return;
    controller.current?.abort();
    const ctrl = new AbortController();
    controller.current = ctrl;
    if (pollTimer.current !== null) clearTimeout(pollTimer.current);
    pollTimer.current = null;
    pollDue.current = false;
    setState((s) => (s.key === key ? { ...s, loading: true } : { key, data: null, loading: true, error: null, stale: false }));

    loadRef.current(ctrl.signal).then(
      (data) => {
        if (ctrl.signal.aborted) return;
        loaded.current++;
        setState({ key, data, loading: false, error: null, stale: isFromCache(data) });
        if (data !== null && pollRef.current?.(data)) {
          pollTimer.current = setTimeout(() => {
            pollTimer.current = null;
            if (document.visibilityState === 'visible') run();
            else pollDue.current = true;
          }, POLL_INTERVAL_MS);
        }
      },
      (err: unknown) => {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        setState((s) => (s.key === key ? { ...s, loading: false, error: messageOf(err) } : s));
      },
    );
  }, [key]);

  /** Show the locally rebuilt data now; the reload that follows replaces it with the server's. */
  const showLocal = useCallback(() => {
    const prepare = peekRef.current;
    if (key === null || !prepare) return;
    const mine = ++peeks.current;
    const loadedBefore = loaded.current;
    prepare().then(
      (rebuild) => {
        // A load that delivered meanwhile started after the change (see the event handler): newer.
        if (mine !== peeks.current || loaded.current !== loadedBefore) return;
        setState((s) => {
          if (s.key !== key || s.data === null) return s;
          const data = rebuild(s.data);
          if (data === undefined || data === s.data) return s;
          return { ...s, data, stale: isFromCache(data) };
        });
      },
      () => {
        // Nothing to show early; the reload still runs.
      },
    );
  }, [key]);

  useEffect(() => {
    if (key === null) {
      setState({ key: null, data: null, loading: false, error: null, stale: false });
      return;
    }
    run();
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeRef.current(() => {
      if (peekRef.current) {
        // A request still running started before this change: its answer would hide it again.
        controller.current?.abort();
        showLocal();
      }
      if (debounce !== null) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        run();
      }, EVENT_DEBOUNCE_MS);
    });
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && pollDue.current) run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Signed in again over a page kept open (e.g. during a recording): what it shows may be stale.
    const unsubscribeAuth = subscribeAuth((me) => {
      if (me) run();
    });
    return () => {
      unsubscribe();
      unsubscribeAuth();
      document.removeEventListener('visibilitychange', onVisibility);
      if (debounce !== null) clearTimeout(debounce);
      if (pollTimer.current !== null) clearTimeout(pollTimer.current);
      pollTimer.current = null;
      pollDue.current = false;
      controller.current?.abort();
      controller.current = null;
    };
  }, [key, run, showLocal]);

  // Data of a previous key is never shown for a new one.
  const current = state.key === key;
  return {
    data: current ? state.data : null,
    loading: current ? state.loading : key !== null,
    error: current ? state.error : null,
    stale: current ? state.stale : false,
    reload: run,
  };
}

/** Server-side transcription in progress (local, not yet uploaded clips change via outbox events). */
function hasServerWork(doc: ViewDocument): boolean {
  return doc.clips.some((c) => !c.localPending && (c.status === 'queued' || c.status === 'processing'));
}

/**
 * Loads a document (api.getDocument) and polls every 3 s while any clip is queued/processing
 * (stops when hidden / unmounted). On an outbox change event for that document the shown view is
 * first rebuilt with the queued ops (api.rebuildDocument, no request) and then re-loaded.
 */
export function useDocument(id: string | null): AsyncState<ViewDocument> {
  const load = useCallback((signal: AbortSignal) => getDocument(id!, { signal }), [id]);
  const subscribe = useCallback((listener: () => void) => subscribeDocument(id!, listener), [id]);
  const peek = useCallback(async () => {
    const queued = await opsForDocument(id!);
    return (current: ViewDocument) => rebuildDocument(current, queued);
  }, [id]);
  return useLoader(id, load, subscribe, hasServerWork, peek);
}

/** Loads a document list; reloads on any outbox change ("*") and when params change. */
export function useDocuments(params: {
  type?: DocType;
  q?: string;
  limit?: number;
  project?: string;
  order?: NotesOrder;
}): AsyncState<DocumentSummary[]> {
  const key = JSON.stringify({
    type: params.type ?? null,
    q: params.q ?? null,
    limit: params.limit ?? null,
    project: params.project ?? null,
    order: params.order ?? null,
  });
  const load = useCallback(
    (signal: AbortSignal) => {
      const p = JSON.parse(key) as { type: DocType | null; q: string | null; limit: number | null; project: string | null; order: NotesOrder | null };
      return listDocuments(
        { type: p.type ?? undefined, q: p.q ?? undefined, limit: p.limit ?? undefined, project: p.project ?? undefined, order: p.order ?? undefined },
        { signal },
      );
    },
    [key],
  );
  const subscribe = useCallback((listener: () => void) => subscribeDocument('*', listener), []);
  return useLoader(key, load, subscribe);
}

export function useOutboxStatus(): OutboxStatus {
  useEffect(() => {
    // Idempotent; makes sure ops queued in an earlier session are replayed.
    startOutbox();
  }, []);
  return useSyncExternalStore(subscribeStatus, getStatus, getStatus);
}

// ---------- features (one shared request for the whole app) ----------

interface FeaturesSnapshot {
  data: Features | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
}

let featuresSnapshot: FeaturesSnapshot = { data: null, loading: false, error: null, stale: false };
let featuresPromise: Promise<Features> | null = null;
let featuresInFlight: Promise<Features> | null = null;
const featuresListeners = new Set<() => void>();
let featuresWatching = false;

function setFeatures(patch: Partial<FeaturesSnapshot>): void {
  featuresSnapshot = { ...featuresSnapshot, ...patch };
  for (const listener of [...featuresListeners]) listener();
}

/**
 * Change the shown features at once (e.g. the restriction the server just lifted), before the
 * next /api/features answer arrives.
 */
export function patchFeatures(patch: Partial<Features>): void {
  if (featuresSnapshot.data) setFeatures({ data: { ...featuresSnapshot.data, ...patch } });
}

/** (Re)load /api/features. Concurrent callers share one in-flight request. */
export function refreshFeatures(force = true): Promise<Features> {
  if (featuresPromise && !force) return featuresPromise;
  setFeatures({ loading: true });
  const promise: Promise<Features> = getFeatures().then(
    (features) => {
      if (featuresPromise === promise) {
        setFeatures({ data: features, loading: false, error: null, stale: isFromCache(features) });
      }
      return features;
    },
    (err: unknown) => {
      if (featuresPromise === promise) {
        // Let the next mount retry instead of keeping the failure forever.
        featuresPromise = null;
        setFeatures({ loading: false, error: messageOf(err) });
      }
      throw err;
    },
  );
  featuresPromise = promise;
  featuresInFlight = promise;
  const done = () => {
    if (featuresInFlight === promise) featuresInFlight = null;
  };
  promise.then(done, done);
  return promise;
}

/** The /api/features request running now, or a new one (for the outbox's session gate). */
export function featuresForSession(): Promise<Features> {
  return featuresInFlight ?? refreshFeatures();
}

function watchAuthChanges(): void {
  if (featuresWatching) return;
  featuresWatching = true;
  let authRequired = getStatus().authRequired;
  // A 401 during replay or a login/logout changes what /api/features reports.
  subscribeStatus((s) => {
    if (s.authRequired === authRequired) return;
    authRequired = s.authRequired;
    refreshFeatures().catch(() => {});
  });
  subscribeAuth((me) => {
    // Signed in: show the new user at once (the page of a previous user must not reappear while
    // the features are re-read).
    if (me && featuresSnapshot.data) setFeatures({ data: featuresWithSession(featuresSnapshot.data, me), stale: false });
    refreshFeatures().catch(() => {});
  });
  // A 401 / `restricted` answer during replay, or a login / logout in another tab.
  subscribeSessionChanged(() => {
    refreshFeatures().catch(() => {});
  });
}

function featuresWithSession(features: Features, me: MeResponse): Features {
  return { ...features, authRequired: true, authenticated: true, setupRequired: false, user: me.user, restricted: me.restricted };
}

function subscribeFeatures(listener: () => void): () => void {
  featuresListeners.add(listener);
  return () => {
    featuresListeners.delete(listener);
  };
}

const getFeaturesSnapshot = () => featuresSnapshot;
const reloadFeatures = () => {
  refreshFeatures().catch(() => {});
};

/** Loads /api/features once per app start (re-exported state is shared across components). */
export function useFeatures(): AsyncState<Features> {
  const snapshot = useSyncExternalStore(subscribeFeatures, getFeaturesSnapshot, getFeaturesSnapshot);
  useEffect(() => {
    watchAuthChanges();
    if (!featuresPromise) refreshFeatures(false).catch(() => {});
  }, []);
  return {
    data: snapshot.data,
    loading: snapshot.loading || (!snapshot.data && !snapshot.error),
    error: snapshot.error,
    stale: snapshot.stale,
    reload: reloadFeatures,
  };
}

// ---------- device owner ----------

/** `failed`: the device's data could not be read yet; the check is repeated (see useOwnership). */
export type OwnershipState = { status: 'checking'; failed?: boolean } | Ownership;

/** Waits between repeated checks of the device's data while storage cannot be read. */
const OWNERSHIP_RETRY_MS = [500, 1_000, 2_000, 5_000, 10_000] as const;

/**
 * Whether the data on this device may be shown to `user` (session.adoptUser): 'checking' until
 * that is known, 'foreign' while another user's unsent work is still here. Always 'ok' without a
 * signed-in user (open mode, login page). When the device's data cannot be read, the answer stays
 * 'checking' (with `failed`) and the check is repeated – it never turns into 'ok' on a guess.
 * `recheck` runs the check again at once (after the previous user's data was discarded).
 */
export function useOwnership(user: User | null): { state: OwnershipState; recheck: () => void } {
  const userId = user?.id ?? null;
  const [result, setResult] = useState<{ userId: string; ownership: Ownership } | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  });
  useEffect(() => {
    const current = userRef.current;
    if (userId === null || !current) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const check = () => {
      adoptUser(current).then(
        (ownership) => {
          if (cancelled) return;
          setFailedFor(null);
          setResult({ userId, ownership });
        },
        (err: unknown) => {
          console.warn('[session] device owner check failed', err);
          if (cancelled) return;
          setFailedFor(userId);
          timer = setTimeout(check, OWNERSHIP_RETRY_MS[Math.min(attempt++, OWNERSHIP_RETRY_MS.length - 1)]);
        },
      );
    };
    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userId, round]);
  const recheck = useCallback(() => setRound((r) => r + 1), []);
  let state: OwnershipState;
  if (userId === null) state = { status: 'ok' };
  // A recheck keeps the previous answer meanwhile (no flash of the loading page).
  else if (result && result.userId === userId) state = result.ownership;
  else state = failedFor === userId ? { status: 'checking', failed: true } : { status: 'checking' };
  return { state, recheck };
}

// ---------- recorder ----------

export interface RecorderHook {
  state: RecorderState;
  /** Updated ~4x per second while recording. */
  elapsedMs: number;
  /**
   * Input level 0..1, re-rendered at most ~15x per second and only while recording (0 otherwise).
   * A meter that animates itself should poll `getLevel()` instead, without re-rendering the page.
   */
  level: number;
  /** Current input level 0..1, read straight from the recorder (cheap; for rAF loops). */
  getLevel: () => number;
  /** Slovenian message of the last failure (microphone, start() – also failures the recorder does not report). */
  error: string | null;
  /** Set when the recording was cut short by the system (track ended). */
  interrupted: boolean;
  /** The system muted the microphone (e.g. a call on iOS): silence is recorded meanwhile (see MSG_MUTED). */
  muted: boolean;
  supported: boolean;
  prepare: () => Promise<void>;
  start: (recoveryMeta?: Record<string, unknown>) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<RecordingResult>;
  cancel: () => Promise<void>;
  release: () => void;
}

const ELAPSED_INTERVAL_MS = 250;
/** The level state re-renders the whole page: ~15 Hz is plenty for a meter. */
const LEVEL_RENDER_INTERVAL_MS = 66;
/** start() failed with something that is not an Error with a message. */
const MSG_START_FAILED = 'Snemanja ni bilo mogoče začeti.';

/** React wrapper around one AudioRecorder instance (released on unmount). */
export function useRecorder(profile: RecorderProfile): RecorderHook {
  const ref = useRef<AudioRecorder | null>(null);
  if (ref.current === null || ref.current.profile !== profile) ref.current = new AudioRecorder({ profile });
  const recorder = ref.current;

  const [state, setState] = useState<RecorderState>(recorder.state);
  const [error, setError] = useState<string | null>(recorder.error);
  const [interrupted, setInterrupted] = useState(false);
  const [muted, setMuted] = useState(recorder.muted);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [supported] = useState(isRecordingSupported);

  /** 'error' events seen, so start() can tell whether the recorder reported its failure itself. */
  const errorEvents = useRef(0);

  useEffect(() => {
    const onState = () => {
      setState(recorder.state);
      setElapsedMs(recorder.elapsedMs);
    };
    const onError = () => {
      errorEvents.current++;
      setError(recorder.error);
    };
    const onInterrupted = () => setInterrupted(true);
    const onMuted = () => setMuted(recorder.muted);
    recorder.addEventListener('state', onState);
    recorder.addEventListener('error', onError);
    recorder.addEventListener('interrupted', onInterrupted);
    recorder.addEventListener('muted', onMuted);
    onState();
    onMuted();
    return () => {
      recorder.removeEventListener('state', onState);
      recorder.removeEventListener('error', onError);
      recorder.removeEventListener('interrupted', onInterrupted);
      recorder.removeEventListener('muted', onMuted);
      recorder.release();
    };
  }, [recorder]);

  useEffect(() => {
    if (state !== 'recording') return;
    const timer = setInterval(() => setElapsedMs(recorder.elapsedMs), ELAPSED_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [recorder, state]);

  useEffect(() => {
    // Not while merely 'ready' (the inspection page keeps the microphone open between entries for
    // hours): polling there would re-render the whole page every frame for a dimmed meter.
    if (state !== 'recording') {
      setLevel(0);
      return;
    }
    let frame = 0;
    let last = -1;
    let renderedAt = -Infinity;
    const tick = (now: number) => {
      // Read every frame: the recorder's smoothing is tuned for ~60 polls per second.
      const next = recorder.level;
      // Skip renders nobody can see, and never re-render the page more than ~15 times a second.
      if (Math.abs(next - last) >= 0.005 && now - renderedAt >= LEVEL_RENDER_INTERVAL_MS) {
        last = next;
        renderedAt = now;
        setLevel(next);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [recorder, state]);

  const getLevel = useCallback(() => recorder.level, [recorder]);

  const prepare = useCallback(async () => {
    setError(null);
    await recorder.prepare();
  }, [recorder]);

  const start = useCallback(
    async (recoveryMeta?: Record<string, unknown>) => {
      setError(null);
      setInterrupted(false);
      const reported = errorEvents.current;
      // The crash-recovery copy belongs to the signed-in user (never offered to someone else).
      const userId = actingUserId();
      try {
        await recorder.start(userId ? { ...recoveryMeta, userId } : recoveryMeta);
      } catch (err) {
        // Not every failure is reported as an 'error' event (e.g. "Snemanje že poteka."): `error`
        // carries it all the same, so pages need not tell them apart.
        if (errorEvents.current === reported) setError(err instanceof Error && err.message ? err.message : MSG_START_FAILED);
        throw err;
      }
    },
    [recorder],
  );

  const pause = useCallback(() => recorder.pause(), [recorder]);
  const resume = useCallback(() => recorder.resume(), [recorder]);
  const stop = useCallback(() => recorder.stop(), [recorder]);
  const cancel = useCallback(async () => {
    await recorder.cancel();
    setInterrupted(false);
  }, [recorder]);
  const release = useCallback(() => recorder.release(), [recorder]);

  return {
    state,
    elapsedMs,
    level,
    getLevel,
    error,
    interrupted,
    muted,
    supported,
    prepare,
    start,
    pause,
    resume,
    stop,
    cancel,
    release,
  };
}

// ---------- environment ----------

function subscribeOnline(listener: () => void): () => void {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}

/** navigator.onLine with online/offline listeners. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine !== false,
    () => true,
  );
}

/** Slovenian warning when the browser cannot store data durably (see localdb.storageWarning). */
export function useStorageWarning(): string | null {
  return useSyncExternalStore(subscribeStorageWarning, storageWarning, () => null);
}
